import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { useLocalStorage } from '@/hooks/useLocalStorage';

/**
 * Shared state for the desktop sidebar collapsed/expanded status.
 * Centralizes the boolean so multiple components re-render in sync
 * (e.g., Sidebar and AssistantContainer). State persists via localStorage.
 */
export type SidebarCollapseState = {
	isCollapsed: boolean;
	hasSidebar: boolean;
	toggleCollapse: () => void;
	collapse: () => void;
	expand: () => void;
};

const SidebarCollapseContext = createContext<SidebarCollapseState | undefined>(undefined);

export const SidebarCollapseProvider: React.FC<{ children: React.ReactNode; hasSidebar?: boolean }> = ({
	children,
	hasSidebar = true,
}) => {
	// Single source of truth stored once in the provider
	const [isCollapsed, setIsCollapsed] = useLocalStorage<boolean>('sidebar_collapsed', false);

	const toggleCollapse = useCallback(() => setIsCollapsed((prev) => !prev), [setIsCollapsed]);
	const collapse = useCallback(() => setIsCollapsed(true), [setIsCollapsed]);
	const expand = useCallback(() => setIsCollapsed(false), [setIsCollapsed]);

	const value = useMemo<SidebarCollapseState>(
		() => ({ isCollapsed, hasSidebar, toggleCollapse, collapse, expand }),
		[isCollapsed, hasSidebar, toggleCollapse, collapse, expand],
	);

	return <SidebarCollapseContext.Provider value={value}>{children}</SidebarCollapseContext.Provider>;
};

/**
 * Hook to access the shared sidebar collapse state. Must be used within provider.
 */
export const useSidebarCollapseContext = () => {
	const ctx = useContext(SidebarCollapseContext);
	if (!ctx) {
		throw new Error('useSidebarCollapseContext must be used within SidebarCollapseProvider');
	}
	return ctx;
};
